import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoaderModule } from '@progress/kendo-angular-indicators';
import { catchError, firstValueFrom, of, retry, take, timeout, timer } from 'rxjs';
import { AfRoutes } from '../../routes';
import { CustomerService } from '../../customers/services/customer.service';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';
import { StorageKeys } from '../../services/wrapper/storage-keys';
import { LogoutState, LogoutStatus } from '../logout/logout-state';

@Component({
  selector: 'af-signout',
  imports: [LoaderModule],
  templateUrl: './signout.component.html',
  styleUrl: './signout.component.scss',
})
export class SignOutComponent implements OnInit {

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly customerService: CustomerService,
    private readonly router: Router
  ) {}

  // If browser restores this page from bfcache, ngOnInit will not run again,
  // so we need to navigate to the logout page from here
  @HostListener('window:pageshow', ['$event'])
  onWindowPageShow(event: PageTransitionEvent): void {
    if (event.persisted) {
      void this.router.navigate([AfRoutes.logout], { replaceUrl: true });
    }
  }

  ngOnInit(): void {
    this.runSignOut();
  }

  private async runSignOut(): Promise<void> {
    let idpLogoutUrl: string | undefined;

    try {
      const oidcEnabled = await firstValueFrom(
        this.authenticationService.isOidcEnabled().pipe(
          take(1),
          catchError(() => of(false))
        )
      );

      if (oidcEnabled) {
        const customerIdPConfig = await firstValueFrom(
          this.customerService.getAssignedIdPConfiguration().pipe(
            timeout(3000),
            retry({
              count: 1,
              delay: (error) => {
              if (!this.isTransientHttpError(error)) throw error;
                return timer(150);
              }
            }),
            catchError(() => of(null))
          )
        );

        idpLogoutUrl = customerIdPConfig?.logoutUrl;
      }

      this.saveLogoutState({
        status: LogoutStatus.COMPLETED,
        ...(idpLogoutUrl ? { idpLogoutUrl } : {}),
      });

      // On success, Amplify hosted signout redirects to the configured logout URL.
      await this.authenticationService.signOutLocal();

    } catch {
      this.saveLogoutState({
        status: LogoutStatus.FAILED,
        ...(idpLogoutUrl ? { idpLogoutUrl } : {}),
      });

      await this.router.navigate([AfRoutes.logout], {
        replaceUrl: true,
      });
    }
  }

  private saveLogoutState(state: LogoutState): void {
    localStorage.setItem(StorageKeys.logoutState, JSON.stringify(state));
  }

  private isTransientHttpError(error: any): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    // 0 often means network error / CORS / offline
    if (error.status === 0) {
      return true;
    } 

    return [502, 503, 504].includes(error.status);
  }
}
